class LazyVideo extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.video = window.BAO.Utils.getElement('video', { context: this })

    if (!this.video) return

    this.video.setAttribute('playsinline', '')

    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    })

    // Observe the video because the host uses display: contents.
    this.observer.observe(this.video)
  }

  disconnectedCallback () {
    if (this.observer) {
      this.observer.disconnect()
    }

    super.disconnectedCallback?.()
  }

  onEntry ([entry], observer) {
    if (!entry.isIntersecting) return

    if (this.video.dataset?.poster) {
      this.video.setAttribute('poster', this.video.dataset.poster)
    }

    Array.from(this.video.children).forEach((videoSource) => {
      if (videoSource.tagName === 'SOURCE' && videoSource.dataset.src) {
        videoSource.src = videoSource.dataset.src
      }
    })

    this.video.load()

    observer.unobserve(this.video)

    this.setAttribute('loaded', '')

    if (this.closest('[data-paused="true"]')) {
      this.video.pause()
      return
    }

    if (
      window.BAO.MediaBreakpoints.isSm ||
      this.video.hasAttribute('autoplay') === false
    ) {
      return
    }

    if (this.video.paused) {
      this.video.play().catch(() => {
        // Autoplay may be blocked by the browser.
      })
    }
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo)
}

class LazyVideoControls extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    if (this.els.play?.element) {
      this.handlePlayClick = this.handlePlayClick.bind(this)

      this.listeners.add(this.els.play.element, 'click', this.handlePlayClick)
    }

    if (this.els.unmute?.element) {
      this.handleUnmuteClick = this.handleUnmuteClick.bind(this)

      this.listeners.add(
        this.els.unmute.element,
        'click',
        this.handleUnmuteClick,
      )
    }
  }

  async handlePlayClick (event) {
    const section = event.target.closest(this?.externalEls?.section?.selector)

    if (!section) return

    const videos = Array.from(section.querySelectorAll('lazy-video')).filter(
      (el) => el.hasAttribute('loaded') && el.video,
    )

    if (!videos.length) return

    const isPlaying = videos.some((el) => !el.video.paused)
    const shouldPlay = !isPlaying

    if (shouldPlay) {
      section.setAttribute('data-paused', 'false')

      const results = await Promise.allSettled(
        videos.map((el) => {
          const video = el.video

          video.setAttribute('playsinline', '')

          return video.play()
        }),
      )

      const hasPlayingVideo = videos.some((el) => !el.video.paused)

      if (!hasPlayingVideo) {
        section.setAttribute('data-paused', 'true')
      }

      this.updatePlayPauseButton(hasPlayingVideo, section)

      results.forEach((result) => {
        if (result.status === 'rejected') {
          console.warn('Video playback failed:', result.reason)
        }
      })
    } else {
      section.setAttribute('data-paused', 'true')

      videos.forEach((el) => {
        el.video.pause()
      })

      this.updatePlayPauseButton(false, section)
    }
  }

  handleUnmuteClick (event) {
    const section = event.target.closest(this?.externalEls?.section?.selector)

    if (!section) return

    const videos = Array.from(section.querySelectorAll('lazy-video')).filter(
      (el) => el.hasAttribute('loaded') && el.video,
    )

    if (!videos.length) return

    const shouldMute = videos.some((el) => !el.video.muted)

    videos.forEach((el) => {
      el.video.muted = shouldMute
    })

    this.updateMuteButton(shouldMute, section)
  }

  updatePlayPauseButton (isPlaying, section) {
    const buttons = section.querySelectorAll(this.els.play.selector)

    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', isPlaying ? 'false' : 'true')

      const playText = button.querySelector(this.els['play-text'].selector)

      if (playText) {
        playText.textContent = isPlaying
          ? this.dataset.pause
          : this.dataset.play
      }
    })
  }

  updateMuteButton (isMuted, section) {
    const buttons = section.querySelectorAll(this.els.unmute.selector)

    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', isMuted ? 'false' : 'true')

      const muteText = button.querySelector(this.els['unmute-text'].selector)

      if (muteText) {
        muteText.textContent = isMuted
          ? this.dataset.unmute
          : this.dataset.mute
      }
    })
  }
}

if (!customElements.get('lazy-video-controls')) {
  customElements.define('lazy-video-controls', LazyVideoControls)
}
